import { FC } from 'react';

import { useTranslations } from '@telialt/common-cms/src/useTranslations';
import { useInitialLeasingPaymentMutation } from '@telialt/core-eshop/src/hooks/mutations/useInitialLeasingPaymentMutation';
import { useCartQuery } from '@telialt/core-eshop/src/hooks/queries/useCartQuery';
import { IncrementInput } from '@telialt/core-eshop/src/product/IncrementInput/IncrementInput';
import { ESHOP_TEST_ID } from '@telialt/core-eshop/src/test/test-ids';
import { LeasingPlanAvailability } from '@telialt/models/src/EshopModel';
import {
    selectInitialPayment,
    selectLeasingPlan,
    selectLeasingPlansAvailability,
    selectMaxInitialPayment,
} from '@telialt/utils/src/eshopSelectors';
import { withParams } from '@telialt/utils/src/helpers';
import { useComponentId } from '@telialt/utils/src/hooks';

import { CalculatorPriceRange, InitialPaymentCalculatorWrapper } from './InitialPaymentCalculator.styles';

type Props = { order?: number };

export const InitialPaymentCalculator: FC<Props> = ({ order }) => {
    const { t } = useTranslations();
    const { cart } = useCartQuery();

    const { mutateInitialLeasingPayment } = useInitialLeasingPaymentMutation();

    const initialPayment = selectInitialPayment(cart) ?? 0;
    const selectedLeasingPlan = selectLeasingPlan(cart);
    const leasingPlanAvailability = selectLeasingPlansAvailability(cart);

    const maxInitialPayment = selectMaxInitialPayment(cart);
    const minInitialPayment = selectedLeasingPlan?.minimalInitialPayment;

    const initialPaymentCalculatorId = useComponentId('initial-payment-calculator');

    if (!selectedLeasingPlan || leasingPlanAvailability !== LeasingPlanAvailability.Available) {
        return null;
    }

    return (
        <InitialPaymentCalculatorWrapper data-test={ESHOP_TEST_ID.INITIAL_PAYMENT_CALCULATOR} style={{ order }}>
            <div id={initialPaymentCalculatorId}>
                {t['eshop.product_page.leasing_selector.calculator.initial_payment']}
            </div>
            <IncrementInput
                minValue={minInitialPayment?.value}
                maxValue={maxInitialPayment?.value}
                paymentInCart={initialPayment}
                onChange={mutateInitialLeasingPayment}
                ariaLabelledBy={initialPaymentCalculatorId}
            />
            <CalculatorPriceRange>
                <span>
                    {withParams(t['eshop.product_page.leasing_selector.calculator.min_price'], {
                        price: minInitialPayment?.formattedValue,
                    })}
                </span>
                <span>
                    {withParams(t['eshop.product_page.leasing_selector.calculator.max_price'], {
                        price: maxInitialPayment?.formattedValue,
                    })}
                </span>
            </CalculatorPriceRange>
        </InitialPaymentCalculatorWrapper>
    );
};
